<template>
  <slot v-if="!lazy" />
  <div :data-loading="loading" class="tw:hidden!" v-if="!lazy"></div>
  <div ref="root" class="chart-lazy-loader" v-if="lazy">
    <!-- Once visible, mount the slotted chart content -->
    <slot v-if="visible" />

    <!-- Overlay spinner until visible and data loaded -->
    <div v-if="!visible || loading" class="overlay">
      <v-progress-circular indeterminate size="60" width="6" />
    </div>
    <div :data-loading="loading" class="tw:hidden!"></div>
  </div>
</template>

<script setup lang="ts">
import useRouteQuery from "@/hooks/useRouteQuery";
import { ref, onMounted, onBeforeUnmount } from "vue";

// Accept the chart's own loading flag
const props = defineProps<{ loading: boolean }>();
const { lazy } = useRouteQuery();

// Tracks whether the chart slot has been revealed at least once
const visible = ref(false);
const root = ref<HTMLElement | null>(null);
let observer: IntersectionObserver;

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        visible.value = true; // reveal the chart
        observer.disconnect(); // stop observing
      }
    },
    { threshold: 0.1 } // fire when ~10% visible
  );

  if (root.value) {
    observer.observe(root.value);
  }
});

onBeforeUnmount(() => {
  observer.disconnect();
});
</script>

<style scoped>
.chart-lazy-loader {
  position: relative;
  /* Reserve enough height so container doesn't collapse */
  min-height: 300px;
}

.overlay {
  position: absolute;
  inset: 0; /* shorthand for top:0; right:0; bottom:0; left:0 */
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
}
</style>
